// src/components/mitchRoom/revenueAnalytics/ForecastBridge.js
//
// Waterfall from what is banked to the forecast — the chart that explains
// WHY the month's number is what it is:
//   MTD → + expected renewals → + new business → + tail of new → − refunds → forecast
// Expected renewals is already the haircut figure (committed × P(alive) ×
// P(collected)); the haircut itself is printed beneath so the gap between
// "scheduled" and "expected" is visible.
import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { formatCurrency, formatAxisCurrency } from 'utils/format';

const COLORS = {
  actual: '#1072CA',
  renewals: '#eda100',
  new: '#1baf7a',
  tail: '#59A14F',
  leakage: '#E15759',
  forecast: '#254A73',
};

export default function ForecastBridge({ month, label }) {
  const steps = useMemo(() => {
    if (!month) return [];
    const actual = month.actual ?? 0;
    const renewals = month.expected_renewals ?? 0;
    const nb = month.new_business ?? 0;
    const tail = month.tail_of_new ?? 0;
    const leak = month.leakage ?? 0;
    const forecast = month.forecast ?? actual + renewals + nb + tail - leak;
    let running = 0;
    const mk = (name, value, color, total = false) => {
      const base = total ? 0 : value >= 0 ? running : running + value;
      const bar = total ? value : Math.abs(value);
      running = total ? value : running + value;
      return { name, value, base, bar, color, total };
    };
    return [
      mk('Banked', actual, COLORS.actual, true),
      mk('Expected renewals', renewals, COLORS.renewals),
      mk('New business', nb, COLORS.new),
      mk('Tail of new', tail, COLORS.tail),
      mk('Refunds', -leak, COLORS.leakage),
      mk('Forecast', forecast, COLORS.forecast, true),
    ];
  }, [month]);

  if (!month) return null;
  const haircut = (month.committed ?? 0) - (month.expected_renewals ?? 0);

  return (
    <div className="rounded-token-large border border-borderGrey bg-white p-4">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-token-xl font-semibold">How the forecast is built{label ? ` — ${label}` : ''}</h3>
        <p className="text-token-xs text-muted-foreground">
          Scheduled {formatCurrency(month.committed ?? 0, { whole: true })} → expected{' '}
          {formatCurrency(month.expected_renewals ?? 0, { whole: true })}
          {haircut > 0 ? ` (haircut ${formatCurrency(haircut, { whole: true })} for cancellations and failed collection)` : ''}
        </p>
      </div>
      <div className="h-[240px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={steps} margin={{ top: 18, right: 16, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#DADADA" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
            <YAxis tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} width={58} />
            <Tooltip
              formatter={(v, n, item) => {
                if (n === 'base') return [null, null];
                const row = item?.payload;
                return [formatCurrency(row?.value ?? v), row?.name];
              }}
              labelFormatter={() => ''}
            />
            <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
            <Bar dataKey="bar" stackId="w" radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {steps.map((s) => (
                <Cell key={s.name} fill={s.color} />
              ))}
              <LabelList
                dataKey="value"
                position="top"
                formatter={(v) => `${v < 0 ? '−' : ''}${formatCurrency(Math.abs(v), { whole: true })}`}
                style={{ fontSize: 10, fill: '#3c3c3c' }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-1 text-token-xs text-muted-foreground">
        Expected renewals = every scheduled invoice × P(still subscribed at its bill date) ×
        P(collected). New business = last year&apos;s units for the rest of the month at today&apos;s prices,
        scaled by this season&apos;s new-customer run rate. Tail of new = renewals seeded by those new
        monthly subs. Refunds at the trailing-12-month rate.
      </p>
    </div>
  );
}
